import { createEffect, createMemo, createSignal, type Accessor } from "solid-js"
import isIdListSame from "../../rx/signal/equals/isIdListSame"
import { isPlainObjDeeplyEqual } from "../isPlainObjDeeplyEqual"

export type QuadrantBound = {
  x: number
  y: number
  width: number
  height: number
}

enum QuadChunkMode {
  ZeroChunks = 0,
  OneChunk = 1,
  FourChunks = 4,
}

interface EntityLike {
  id: number,
  x: number,
  y: number,
}

type EntityQuadtreeEffectFn<T extends EntityLike> = (quadtree: Accessor<Array<T>>, quadrant: QuadrantBound) => void

export default function createEntityQuadtreeEffect<T extends EntityLike>(
  getParentPoints: Accessor<Array<T>>,
  getParentQuadrantBound: Accessor<QuadrantBound>,
  maxChunkSize: number,
  maxNesting: number,
  effectFn: EntityQuadtreeEffectFn<T>,
): void {
  // untrack(() => {
  //   console.log('createEntityQuadtreeEffect.init', deepClone({
  //     quadrant: getParentQuadrantBound(),
  //     maxNesting,
  //     points: getParentPoints(),
  //   }))
  // })

  const [getQuadrantBoundList, setQuadrantBoundList] = createSignal<Array<QuadrantBound>>([], {
    equals: isPlainObjDeeplyEqual,
  })
  // createEffect(() => {
  //   const list = getQuadrantBoundList()
  //   console.log('createEntityQuadtreeEffect.getQuadrantBoundList', list)
  // })

  const quadChunkMode = createMemo(() => {
    const points = getParentPoints()
    if (points.length > maxChunkSize && maxNesting > 0) {
      return QuadChunkMode.FourChunks
    } else if (points.length > 0) {
      return QuadChunkMode.OneChunk
    } else {
      return QuadChunkMode.ZeroChunks
    }
  }, QuadChunkMode.ZeroChunks)

  // createEffect(() => {
  //   console.log('createEntityQuadtreeEffect.quadChunkMode', quadChunkMode())
  // })

  createEffect(() => {
    // console.log('createEntityQuadtreeEffect.effect1')
    const qcMode = quadChunkMode()
    if (qcMode === QuadChunkMode.FourChunks) {
      const { x, y, width, height } = getParentQuadrantBound()
      const hw = width / 2
      const hh = height / 2
      const quadrants = [
        { x: x, y: y, width: hw, height: hh }, // top-left
        { x: x + hw, y: y, width: hw, height: hh }, // top-right
        { x: x, y: y + hh, width: hw, height: hh }, // bottom-left
        { x: x + hw, y: y + hh, width: hw, height: hh }, // bottom-right
      ]
      setQuadrantBoundList(quadrants)
    } else if (qcMode === QuadChunkMode.OneChunk) {
      setQuadrantBoundList([getParentQuadrantBound()])
    } else {
      // no action required if points are zero
      setQuadrantBoundList([])
    }
  })

  const [getPointsChunks, setPointsChunks] = createSignal<Array<Array<T>>>([])

  // createEffect(() => {
  //   const list = getPointsChunks()
  //   console.log('createEntityQuadtreeEffect.getPointsChunks', list.length)
  // })

  createEffect(() => {
    // console.log('createEntityQuadtreeEffect.effect2')
    const qcMode = quadChunkMode()
    if (qcMode === QuadChunkMode.FourChunks) {
      const childrenPoints: Array<Array<T>> = [[], [], [], []]

      const { x, y, width, height } = getParentQuadrantBound()
      const hw = width / 2
      const hh = height / 2

      for (const point of getParentPoints()) {
        const i = (point.x >= x + hw ? 1 : 0) + (point.y >= y + hh ? 2 : 0)
        childrenPoints[i].push(point)
      }
      setPointsChunks(childrenPoints)
    } else {
      setPointsChunks([getParentPoints()])
    }
  })

  createEffect(() => {
    // console.log('createEntityQuadtreeEffect.effect3')
    const list = getQuadrantBoundList()
    for (const [quadIndex, quadrant] of list.entries()) {
      // if this effect glitches out, try:
      // import { mapArray } from "solid-js"
      createEffect(() => {
        const [getQuadPoints, setQuadPoints] = createSignal<Array<T>>([], {
          equals: isIdListSame,
        })
        createEffect(() => {
          setQuadPoints(getPointsChunks()[quadIndex])
        })
        const exceedsMaxChunkSize = createMemo(() => getQuadPoints().length > maxChunkSize)
        createEffect(() => {
          if (maxNesting > 0 && exceedsMaxChunkSize()) {
            createEntityQuadtreeEffect(
              getQuadPoints,
              () => quadrant,
              maxChunkSize,
              maxNesting - 1,
              effectFn,
            )
          } else {
            effectFn(getQuadPoints, quadrant)
          }
        })
      })
    }
  })
}
